import { useGamesSearch } from "entities/game/hooks/useGamesSearch";
import { GameOption } from "entities/game/types";
import { ModuleGameStatus, SelectedGame } from "entities/modules/types";
import React, { FC, useCallback, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";
import AutocompleteTag from "shared/components/AutocompleteTag";
import AutocompleteField from "shared/components/Form/AutocompleteField";
import TextField from "shared/components/Form/TextField";
import FieldLabel from "shared/components/Form/components/FieldLabel";
import ListboxComponent from "shared/components/ListBox";
import { COLOR_BLACK, COLOR_GREY_6 } from "shared/theme/colors";

type Props = {
  selectedGamesManually: SelectedGame[];
  setSelectedGamesManually: (games: SelectedGame[]) => void;
  formatSelectedGamesHints: (game: GameOption) => string;
  tagSelectedGameIds?: string[];
  availableStudios?: { id: number; name: string }[];
  studioId?: string;
};

const AddManualGamesField: FC<Props> = ({
  selectedGamesManually,
  setSelectedGamesManually,
  formatSelectedGamesHints,
  tagSelectedGameIds = [],
  availableStudios = [],
  studioId,
}) => {
  const [inputValue, setInputValue] = useState("");

  const { games, isLoading, onSearchChange } = useGamesSearch({
    isActive: ["true"],
    initialResultsCount: 20,
    searchResultsCount: 50,
    filterTags: studioId ? [`productId:${studioId}`] : [],
  });

  const handleAddGamesManually = useEvent((selectedOptions: GameOption[]) => {
    const newManualSelectedGames: SelectedGame[] = selectedOptions.map(
      (option: GameOption, index: number) => {
        const studio = availableStudios.find(
          (studio) => studio.id === option.productSupplierId,
        );

        return {
          id: option.id.toString(),
          name: option.displayName || option.name || "",
          order: index + 1,
          status: ModuleGameStatus.Active,
          studioId: option.productSupplierId,
          studioName: studio?.name || option.productName || "",
        };
      },
    );
    setSelectedGamesManually(newManualSelectedGames);
  });

  const handleAutocompleteChange = useEvent(
    (_: React.SyntheticEvent<Element, Event>, newValue: GameOption[]) => {
      handleAddGamesManually(newValue);
    },
  );

  const handleInputChange = useEvent(
    (_: React.SyntheticEvent<Element, Event>, value: string) => {
      setInputValue(value);
      onSearchChange(value);
    },
  );

  const getOptionDisabled = useCallback(
    (option: GameOption) => {
      const isTagAdded = tagSelectedGameIds.includes(option.id.toString());
      const isManuallyAdded = selectedGamesManually.some(
        (game) => game.id === option.id.toString(),
      );

      return Boolean(isTagAdded && !isManuallyAdded);
    },
    [tagSelectedGameIds, selectedGamesManually],
  );

  const selectedManualGames: GameOption[] = useMemo(() => {
    return selectedGamesManually
      .map((selectedGame) => {
        const gameInResults = games.find(
          (game) => game.id.toString() === selectedGame.id,
        );

        if (gameInResults) {
          return {
            ...gameInResults,
            name: gameInResults.displayName || gameInResults.name || "",
          } as GameOption;
        }

        return {
          id: selectedGame.id,
          name: selectedGame.name,
          displayName: selectedGame.name,
          productSupplierId: selectedGame.studioId,
          productName: selectedGame.studioName,
        } as unknown as GameOption;
      })
      .filter((game): game is GameOption => game !== undefined);
  }, [selectedGamesManually, games]);

  const gameOptions = useMemo(() => {
    return games.map((game) => ({
      ...game,
      name: game.displayName || game.name || "",
      addedByTag: tagSelectedGameIds.includes(game.id.toString()),
      addedManually: selectedGamesManually.some(
        (selectedGame) => selectedGame.id === game.id.toString(),
      ),
    })) as GameOption[];
  }, [games, tagSelectedGameIds, selectedGamesManually]);

  return (
    <AutocompleteField
      slotProps={{
        paper: {
          sx: {
            overflow: "visible",
          },
        },
      }}
      multiple
      freeSolo
      filterSelectedOptions={false}
      ListboxComponent={ListboxComponent}
      disableCloseOnSelect
      borderColor={COLOR_GREY_6}
      options={gameOptions}
      value={selectedManualGames}
      onChange={handleAutocompleteChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      getOptionLabel={(option) => {
        return option?.name || "";
      }}
      isOptionEqualToValue={(option, value) =>
        option?.id?.toString() === value?.id?.toString()
      }
      getOptionDisabled={getOptionDisabled}
      loading={isLoading}
      renderOption={(props, option: GameOption, { selected }) => {
        const isAddedByTag = !!option.addedByTag;
        const isAddedManually = !!option.addedManually;

        return (
          <AutocompleteTag
            {...props}
            key={option.id}
            selected={selected || isAddedByTag}
            disabled={isAddedByTag && !isAddedManually}
            isAddedByTag={isAddedByTag}
            isAddedManually={isAddedManually}
            optionName={option.name || ""}
            hintText={formatSelectedGamesHints(option)}
          />
        );
      }}
      renderInput={(params) => (
        <>
          <FieldLabel label="Add Game manually" labelColor={COLOR_BLACK} />
          <TextField {...params} placeholder="Search and select games to add" />
        </>
      )}
    />
  );
};

export default AddManualGamesField;
